import React, { useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';

const FeedbackDialog = ({ open, onClose, feedbackRequest, backendUrl, authTokens }) => {
    const [npsScore, setNpsScore] = useState(null);
    const [feedbackText, setFeedbackText] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async () => {
        if (npsScore === null) {
            setError('Please select a rating');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const response = await fetch(`${backendUrl}api/submit-nps-feedback/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${authTokens.access}`,
                },
                body: JSON.stringify({
                    request_id: feedbackRequest.request_id,
                    nps_score: npsScore,
                    feedback_text: feedbackText.trim() || null,
                }),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.error || 'Failed to submit feedback');
            }

            // Close dialog after successful submission
            handleClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setNpsScore(null);
        setFeedbackText('');
        setError('');
        onClose();
    };

    const handleSkip = () => {
        handleClose();
    };

    return (
        <Dialog open={open} onClose={handleSkip} maxWidth="sm" fullWidth>
            <DialogTitle>
                {feedbackRequest?.title || 'We value your feedback!'}
            </DialogTitle>
            <DialogContent>
                {error && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {error}
                    </Alert>
                )}

                {feedbackRequest?.message && (
                    <Typography variant="body2" sx={{ marginBottom: 3, color: 'text.secondary' }}>
                        {feedbackRequest.message}
                    </Typography>
                )}

                <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: 'bold' }}>
                    How likely would you recommend this app to a friend?
                </Typography>

                <Box sx={{ display: 'flex', justifyContent: 'center', marginBottom: 3 }}>
                    <ToggleButtonGroup
                        value={npsScore}
                        exclusive
                        onChange={(e, value) => value !== null && setNpsScore(value)}
                        aria-label="NPS rating"
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(10, 1fr)',
                            gap: 0.5,
                            '& .MuiToggleButton-root': {
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1,
                                '&.Mui-selected': {
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    '&:hover': {
                                        backgroundColor: 'primary.dark',
                                    }
                                }
                            }
                        }}
                    >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                            <ToggleButton key={num} value={num} aria-label={`Rating ${num}`}>
                                {num}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                        Not likely
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Very likely
                    </Typography>
                </Box>

                <TextField
                    label="Additional feedback (optional)"
                    value={feedbackText}
                    onChange={(e) => setFeedbackText(e.target.value)}
                    fullWidth
                    multiline
                    rows={4}
                    margin="normal"
                    placeholder="Tell us what you think..."
                    inputProps={{ maxLength: 1000 }}
                    helperText={`${feedbackText.length}/1000`}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={handleSkip} disabled={loading}>
                    Skip
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || npsScore === null}
                >
                    {loading ? 'Submitting...' : 'Submit'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackDialog;
